// @flow
import React, { Component } from 'react';
import _ from 'lodash';
import { customValidate } from '../form/CustomValidators';

type Props = {
  model: any,
  constraints?: Object,
  userPermissions?: Object,
  permissionKey?: string,
  permissionType?: string,
  aclFields?: Array<string>,
  onModelChange: Function,
  showAllError?: boolean,
};

export type HasFormError = (control: string, shouldValidate: boolean) => boolean;
export type FormError = object;
export type HandleControlBlur = (event: React.FocusEvent<HTMLInputElement>, data: object) => { name: string, value: string };
export type HandleInputChange = (event: React.FormEvent<HTMLInputElement>, data: object, modelOverride: object) => { name: string, value: string };

export function extractNameAndValue(event: any, data: any) {
  const name = _.get(data, 'name') || _.get(event, 'target.name');
  let value;
  if (!_.isNil(_.get(data, 'checked'))) value = _.get(data, 'checked');
  else if (!_.isNil(_.get(data, 'value'))) value = _.get(data, 'value');
  else if (!_.isNil(_.get(event, 'target.value'))) value = _.get(event, 'target.value');

  if (data && data.type === 'number') {
    return { name, value: Number(value) };
  }

  return { name, value };
}

export const formComponent = (constraints: Object = {}) => (WrappedComponent: any) =>
  (class extends Component {
    constructor(props: Props) {
      super(props);
      // Merge props constraints if the constraints are passed through props,
      // whether or not they are passed in the decorator
      this.state = {
        dirtyControls: {},
        error: {},
        constraints: _.assign({}, constraints, this.props.constraints),
        extraConstraints: {},
      };
      this.childForms = [];
    }

    componentDidMount() {
      // Add self to parent form
      if (this.props.addToParent) {
        this.props.addToParent(this);
      }
    }

    componentWillUnmount() {
      // Remove self from parent form
      if (this.props.removeFromParent) {
        this.props.removeFromParent(this);
      }
    }

    UNSAFE_componentWillReceiveProps(nextProps: Props) {
      if (nextProps.showAllError) {
        this.validateForm(nextProps.model);
      }
    }

    addToParent = (reactElem: any) => {
      this.childForms.push(reactElem);
    };

    addExtraConstraints = (extraConstraints: Object) => {
      this.setState({ extraConstraints });
    };

    removeFromParent = (reactElem: any) => {
      const index = this.childForms.indexOf(reactElem);
      if (index !== -1) {
        this.childForms.splice(index, 1);
      }
    };

    handleInputChange = (event: any, data: any, modelOverride: Object = {}) => {
      const { name: controlName } = extractNameAndValue(event, data);
      let { value: controlValue } = extractNameAndValue(event, data);
      const { model } = this.props;
      const updatedModel = _.cloneDeep(_.isEmpty(modelOverride) ? model : modelOverride);
      if (typeof controlValue === 'string' && controlName === 'meta.statsKey') {
        controlValue = controlValue.trim();
      }
      _.set(updatedModel, controlName, controlValue);
      if (this.props.onModelChange) {
        this.props.onModelChange(updatedModel);
      }
      return { name: controlName, value: controlValue };
    };

    handleNumberInputChange = (event: any, data: any, modelOverride: Object = {}) => {
      const { name: controlName, value } = extractNameAndValue(event, data);
      const controlValue = Number(value);
      const { model } = this.props;
      const updatedModel = _.cloneDeep(_.isEmpty(modelOverride) ? model : modelOverride);
      _.set(updatedModel, controlName, controlValue);
      if (this.props.onModelChange) {
        this.props.onModelChange(updatedModel);
      }
      return { name: controlName, value: controlValue };
    }

    // Pass control to get errors for a single field
    // Pass no control to get errors for the form
    // Pass shouldValidate to force validate once again rather than picking values from state
    hasError = (control: string, shouldValidate: boolean = false): boolean => {
      // validate returns undefined if there is no error.
      const { error, dirtyControls } = shouldValidate ? this.validateForm() : this.state;
      if (control === '') {
        return !(_.isEmpty(error));
      }
      return !!error[control] && dirtyControls[control];
    };

    customValidate(model: any): Object {
      const { userPermissions, permissionKey, permissionType, aclFields } = this.props;
      const constraints = { ...this.state.constraints, ...this.state.extraConstraints };
      const error = customValidate(model, constraints, userPermissions, permissionKey, permissionType, aclFields) || {};
      return { constraints, error };
    }

    validateForm(model: any): Object {
      const { constraints, error } = this.customValidate(model || this.props.model);
      const dirtyControls = Object.keys(constraints).reduce((results, key) => {
        results[key] = true;
        return results;
      }, {});
      this.setState({ error, dirtyControls });
      return { error, dirtyControls };
    }

    handleControlBlur = (event: React.FocusEvent<HTMLInputElement>, data: object) => {
      const { name: controlName, value } = extractNameAndValue(event, data);
      this.setState(prevState => {
        const dirtyControls = { ...prevState.dirtyControls, [controlName]: true };
        const { error } = this.customValidate({ ...this.props.model, [controlName]: value });
        return { error, dirtyControls };
      });
      return { name: controlName, value };
    };

    clearDirtyControls = (...controls: Array<string>) => {
      this.setState(prevState => {
        const dirtyControls = _.omit(prevState.dirtyControls, controls);
        return { dirtyControls };
      });
    };

    getFormErrors = () => {
      let errors = [];
      const result = this.validateForm();
      errors = [...errors, ...Object.values(result.error).reduce((finalErrors, errors) => {
        return [...errors, ...finalErrors];
      }, [])];
      this.childForms.forEach((childForm) => {
        errors = [...errors, ...childForm.getFormErrors()];
      });
      return errors;
    }

    isValid = (): boolean => {
      // Validate all self and children forms
      // Return valid true or false
      let valid = true;
      const result = this.validateForm();
      if (!_.isEmpty(result.error)) {
        valid = false;
      }

      this.childForms.forEach((childForm) => {
        const isChildValid = childForm.isValid();
        if (!isChildValid) {
          valid = false;
        }
      });

      return valid;
    }

    render() {
      return (
        <WrappedComponent
          {...this.props}
          addToParent={this.addToParent}
          addExtraConstraints={this.addExtraConstraints}
          removeFromParent={this.removeFromParent}
          handleInputChange={this.handleInputChange}
          handleNumberInputChange={this.handleNumberInputChange}
          hasError={this.hasError}
          handleControlBlur={this.handleControlBlur}
          clearDirtyControls={this.clearDirtyControls}
          error={this.state.error}
          showAllError={this.props.showAllError}
          dirtyControls={this.state.dirtyControls}
          isValid={this.isValid}
        />
      );
    }
  });

export default formComponent;
